/**
 * Gamepad + keyboard spatial navigation for overlay menu screens.
 *
 * Polled once per rendered frame (`GamepadNav.update()`, called from
 * `Game.render()` regardless of pause state — menus need navigation even
 * while the sim isn't ticking). While `Game.currentOverlay()` returns an
 * element, D-pad/left-stick moves a focus highlight among that overlay's
 * focusable controls, A/Enter activates the focused one, B triggers Escape,
 * and Start toggles pause. Arrow keys/Enter do the same via native keydown.
 * Outside an overlay (screen === 'run') only Start is read (it opens the
 * pause menu), so in-run keyboard/gamepad movement is untouched. The first
 * connected pad drives menus, whatever its index.
 */

import { isEditableTarget } from '../core/bindings';
import { noteGamepad } from './inputPrompts';

const FOCUSABLE_SELECTOR = 'button:not([disabled]), input, .card';
const REPEAT_FIRST_MS = 250;
const REPEAT_MS = 120;
const STICK_DEADZONE = 0.5;

type Dir = 'up' | 'down' | 'left' | 'right';

export interface NavRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Pick the control that is visually in the requested direction. Candidates
 * sharing the current control's row/column win before diagonal candidates;
 * distances are measured edge-to-edge so a wide card does not distort focus.
 */
export function directionalTarget(current: NavRect, candidates: readonly NavRect[], dir: Dir): number {
  const cx = current.left + current.width / 2;
  const cy = current.top + current.height / 2;
  let best = -1;
  let bestScore = Infinity;
  candidates.forEach((r, i) => {
    const ex = r.left + r.width / 2;
    const ey = r.top + r.height / 2;
    const horizontal = dir === 'left' || dir === 'right';
    const delta = horizontal ? ex - cx : ey - cy;
    if ((dir === 'left' || dir === 'up') ? delta >= -1 : delta <= 1) return;

    const primary = dir === 'right' ? Math.max(0, r.left - current.right)
      : dir === 'left' ? Math.max(0, current.left - r.right)
        : dir === 'down' ? Math.max(0, r.top - current.bottom)
          : Math.max(0, current.top - r.bottom);
    const crossStart = horizontal ? Math.max(current.top, r.top) : Math.max(current.left, r.left);
    const crossEnd = horizontal ? Math.min(current.bottom, r.bottom) : Math.min(current.right, r.right);
    const overlapsLane = crossEnd >= crossStart;
    const crossGap = overlapsLane ? 0 : crossStart - crossEnd;
    const crossCenter = horizontal ? Math.abs(ey - cy) : Math.abs(ex - cx);
    // Lane overlap is the strongest signal, then edge distance and alignment.
    const score = (overlapsLane ? 0 : 1_000_000) + primary * 1_000 + crossGap * 10 + crossCenter;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}

export interface GamepadNavHost {
  /** The currently mounted overlay screen element, or null while running with no overlay. */
  currentOverlay(): HTMLElement | null;
  /** Route a synthetic hotkey the same way a real keydown would (e.g. 'Escape'). */
  pressEscape(): void;
  /** Toggle pause from the 'run' screen (Start button). */
  togglePause(): void;
  /** Buttons bound to pause (default Start = 9); src/core/bindings.ts. */
  pauseButtons?(): readonly number[];
  /** Any newly pressed pad button (resets idle, exits attract/replay). */
  onPadActivity?(): void;
  /** Keys that move menu focus — the player's movement bindings (default WASD + arrows). */
  menuKeys?(): Record<Dir, readonly string[]>;
}

const DEFAULT_MENU_KEYS: Record<Dir, readonly string[]> = { up: ['KeyW', 'ArrowUp'], down: ['KeyS', 'ArrowDown'], left: ['KeyA', 'ArrowLeft'], right: ['KeyD', 'ArrowRight'] };


export class GamepadNav {
  private focused: HTMLElement | null = null;
  private lastDir: Dir | null = null;
  private dirHeldSince = 0;
  private nextRepeatAt = 0;
  private prevButtons = new Set<number>();
  private prevAxisDir: Dir | null = null;
  /** While true (e.g. the Controls panel is waiting for a button), pads drive nothing here. */
  suspended = false;

  constructor(
    private host: GamepadNavHost,
    target: Window = window,
    private getPads: () => ArrayLike<Gamepad | null> = () =>
      typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [],
  ) {
    target.addEventListener('keydown', (e) => this.onKeyDown(e));
  }

  /* ------------------------------------------------------------ keyboard */

  private onKeyDown(e: KeyboardEvent): void {
    const overlay = this.host.currentOverlay();
    if (!overlay) return;
    if (e.repeat || isEditableTarget(e.target)) return;
    const keys = this.host.menuKeys?.() ?? DEFAULT_MENU_KEYS;
    const dir = (Object.keys(keys) as Dir[]).find((d) => keys[d].includes(e.code));
    if (dir) {
      // Sliders keep their native arrow-key behavior.
      if (e.code.startsWith('Arrow') && (e.target as HTMLInputElement | null)?.type === 'range') return;
      e.preventDefault?.();
      this.move(overlay, dir);
      return;
    }
    switch (e.code) {
      case 'Enter':
      case 'NumpadEnter':
        this.activate();
        break;
      default:
        return;
    }
  }

  /* ------------------------------------------------------------- gamepad */

  update(): void {
    const p = Array.from(this.getPads()).find((pad): pad is Gamepad => !!pad) ?? null;
    noteGamepad(p);
    const pressed = (i: number) => p?.buttons[i]?.pressed ?? false;
    const justPressed = (i: number) => pressed(i) && !this.prevButtons.has(i);
    if (p && !this.suspended && p.buttons.some((b, i) => b.pressed && !this.prevButtons.has(i))) this.host.onPadActivity?.();
    const pausePressed = () => (this.host.pauseButtons?.() ?? [9]).some((i) => justPressed(i));
    const rememberButtons = () => {
      this.prevButtons.clear();
      if (p) for (let i = 0; i < p.buttons.length; i++) if (pressed(i)) this.prevButtons.add(i);
    };

    const overlay = this.host.currentOverlay();
    if (!overlay) {
      // In a run: only Start is ours (opens the pause menu); movement stays with core/input.ts.
      this.focused = null;
      this.lastDir = null;
      this.prevAxisDir = null;
      if (!this.suspended && pausePressed()) this.host.togglePause();
      rememberButtons();
      return;
    }
    if (this.focused && (!this.focused.isConnected || !overlay.contains(this.focused))) this.focused = null;
    if (!this.focused) this.focusDefault(overlay);

    if (!p) {
      this.prevButtons.clear();
      return;
    }
    if (this.suspended) {
      rememberButtons();
      this.prevAxisDir = this.readDirection(p);
      return;
    }

    const dir = this.readDirection(p);
    const now = performance.now();
    if (dir) {
      if (dir !== this.prevAxisDir) {
        this.move(overlay, dir);
        this.dirHeldSince = now;
        this.nextRepeatAt = now + REPEAT_FIRST_MS;
      } else if (now >= this.nextRepeatAt) {
        this.move(overlay, dir);
        this.nextRepeatAt = now + REPEAT_MS;
      }
    }
    this.prevAxisDir = dir;

    if (justPressed(0)) this.activate(); // A / Cross
    if (justPressed(1)) this.host.pressEscape(); // B / Circle
    if (pausePressed()) this.host.togglePause(); // Start (or the bound pause button)

    rememberButtons();
  }

  private readDirection(p: Gamepad): Dir | null {
    const ax = p.axes[0] ?? 0;
    const ay = p.axes[1] ?? 0;
    const dpadUp = p.buttons[12]?.pressed ?? false;
    const dpadDown = p.buttons[13]?.pressed ?? false;
    const dpadLeft = p.buttons[14]?.pressed ?? false;
    const dpadRight = p.buttons[15]?.pressed ?? false;
    if (dpadUp) return 'up';
    if (dpadDown) return 'down';
    if (dpadLeft) return 'left';
    if (dpadRight) return 'right';
    if (Math.abs(ax) > Math.abs(ay)) {
      if (ax > STICK_DEADZONE) return 'right';
      if (ax < -STICK_DEADZONE) return 'left';
    } else {
      if (ay > STICK_DEADZONE) return 'down';
      if (ay < -STICK_DEADZONE) return 'up';
    }
    return null;
  }

  /* ------------------------------------------------------------- focus */

  private focusables(overlay: HTMLElement): HTMLElement[] {
    const all = Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    return all.filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  private focusDefault(overlay: HTMLElement): void {
    const list = this.focusables(overlay);
    const preferred = list.find((el) => el.hasAttribute('data-default-focus'));
    const scroll = !overlay.hasAttribute('data-preserve-initial-scroll');
    if (preferred) this.setFocus(preferred, scroll);
    else if (list.length) this.setFocus(list[0], scroll);
  }

  private setFocus(el: HTMLElement, scroll = true): void {
    if (this.focused) this.focused.classList.remove('gp-focus');
    this.focused = el;
    el.classList.add('gp-focus');
    if (scroll) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private move(overlay: HTMLElement, dir: Dir): void {
    const list = this.focusables(overlay);
    if (!list.length) return;
    if (!this.focused || !list.includes(this.focused)) {
      this.setFocus(list[0]);
      return;
    }
    const override = this.focused.dataset[`nav${dir[0].toUpperCase()}${dir.slice(1)}` as keyof DOMStringMap];
    if (override) {
      const target = overlay.querySelector<HTMLElement>(override.startsWith('#') ? override : `#${CSS.escape(override)}`);
      if (target && list.includes(target)) { this.setFocus(target); return; }
    }

    const group = this.focused.closest<HTMLElement>('[data-nav-group]');
    const pool = group ? list.filter((el) => group.contains(el)) : list;
    const candidates = pool.filter((el) => el !== this.focused);
    const target = directionalTarget(this.focused.getBoundingClientRect(), candidates.map((el) => el.getBoundingClientRect()), dir);
    let best = target >= 0 ? candidates[target] : null;
    if (!best && group?.hasAttribute('data-nav-wrap') && candidates.length) {
      const cur = this.focused.getBoundingClientRect();
      const horizontal = dir === 'left' || dir === 'right';
      const lane = candidates.filter((el) => {
        const r = el.getBoundingClientRect();
        return horizontal ? Math.min(cur.bottom, r.bottom) >= Math.max(cur.top, r.top) : Math.min(cur.right, r.right) >= Math.max(cur.left, r.left);
      });
      const wrapPool = lane.length ? lane : candidates;
      wrapPool.sort((a, b) => {
        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();
        return horizontal ? ar.left - br.left : ar.top - br.top;
      });
      best = dir === 'right' || dir === 'down' ? wrapPool[0] : wrapPool[wrapPool.length - 1];
    }
    if (best) this.setFocus(best);
  }

  private activate(): void {
    const overlay = this.host.currentOverlay();
    if (!overlay || !this.focused) return;
    if (!overlay.contains(this.focused)) return;
    this.focused.click();
  }
}
